/**
 * Content kinds beyond native GLSL: ISF shaders (first-party ones bundled, user ones imported
 * into the library) and the MilkDrop preset pack (Butterchurn), wired into the controller.
 */
import { IsfError, isfId, parseIsf } from '../engine/render/isf/parse';
import type { VisualizationDef } from '../shared/viz';
import type { AppController } from './controller.svelte';

const BUILTIN_ISF = import.meta.glob('../content/isf/*.fs', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/**
 * The Butterchurn community preset pack, pinned by version and SHA-256. It contains JavaScript
 * (preset equations), so it is treated like a dependency: one known artifact, verified before use.
 */
export const MILKDROP_PACK = {
  url: 'https://cdn.jsdelivr.net/npm/butterchurn-presets@2.4.7/lib/butterchurnPresets.min.js',
  sha256: '136c746836aef6dff8a1f6a93ede48c7bbe4da27d2f44de6b59ebc56ffefca10',
};

export function isfToViz(id: string, source: string, fallbackName: string): VisualizationDef {
  const parsed = parseIsf(source, fallbackName);
  const def: VisualizationDef = {
    id,
    name: parsed.name,
    description: parsed.description || 'ISF shader',
    params: parsed.params,
    fragment: '',
    kind: 'isf',
    source,
    tags: parsed.categories.map((c) => `style:${c.toLowerCase()}`),
    // ISF has no notion of layering; an "Element" (or "Overlay") category marks shapes on black.
    role: parsed.categories.some((c) => /^(element|overlay)$/i.test(c)) ? 'element' : 'background',
  };
  if (parsed.credit) def.credit = parsed.credit;
  return def;
}

export function builtinIsf(): VisualizationDef[] {
  return Object.entries(BUILTIN_ISF).map(([path, source]) => {
    const file = path.split('/').pop()!.replace(/\.fs$/, '');
    const name = file.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return isfToViz(`isf-${file}`, source, name);
  });
}

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function installContentKinds(ctl: AppController): void {
  const builtin = builtinIsf();
  const broken = new Set<string>();

  ctl.customVizFromLibrary = (sources) => {
    const defs = [...builtin];
    for (const s of sources) {
      try {
        defs.push(isfToViz(s.id, s.source, s.name));
      } catch {
        broken.add(s.id);
      }
    }
    return defs;
  };

  ctl.importIsf = async (fileName, source) => {
    let def: VisualizationDef;
    try {
      def = isfToViz(isfId(fileName, source), source, fileName.replace(/\.\w+$/, ''));
    } catch (err) {
      throw new Error(err instanceof IsfError ? err.message : String(err), { cause: err });
    }
    await ctl.library.saveVisualization({ id: def.id, name: def.name, source });
    ctl.toast(`Added visualization “${def.name}”.`);
  };

  ctl.importButterchurn = async () => {
    throw new Error(
      'MilkDrop preset files contain code, so only the verified community pack can be loaded. Turn on “Classic MilkDrop presets” in Settings → Visual.',
    );
  };

  ctl.loadMilkdrop = async () => {
    if (ctl.milkdropState === 'loading' || ctl.milkdropState === 'ready') return;
    ctl.milkdropState = 'loading';
    try {
      const res = await fetch(MILKDROP_PACK.url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buf = await res.arrayBuffer();
      if ((await sha256Hex(buf)) !== MILKDROP_PACK.sha256)
        throw new Error('integrity check failed');
      ctl.milkdropCode = new TextDecoder().decode(buf);
      if (ctl.ready) ctl.engineLoadMilkdrop(ctl.milkdropCode);
      else ctl.milkdropState = 'off'; // sent to the renderer when audio starts
    } catch (err) {
      ctl.milkdropState = 'failed';
      ctl.toast(
        `Couldn’t load the MilkDrop pack: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  };

  ctl.syncCustomViz();
}
